import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import useSWRSubscription from "swr/subscription";
import { FIRESTORE_INVOICES_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import dayjs from "dayjs";

export function useInvoices(maxDaysAgo = 30, customDateRange = null) {
  const { data, error } = useSWRSubscription(
    `invoices-${maxDaysAgo}-${customDateRange ? `${customDateRange[0]}-${customDateRange[1]}` : "null"}`,
    (key, { next }) => {
      try {
        // Determine date range for filtering
        let startDate, endDate;

        if (customDateRange && customDateRange[0] && customDateRange[1]) {
          // Use custom date range
          startDate = dayjs(customDateRange[0]).format("YYYY-MM-DD");
          endDate = dayjs(customDateRange[1]).format("YYYY-MM-DD");
        } else {
          // Use maxDaysAgo
          startDate = dayjs().subtract(maxDaysAgo, "days").format("YYYY-MM-DD");
          endDate = dayjs().add(30, "days").format("YYYY-MM-DD"); // Include future invoices
        }

        const invoicesCollection = collection(
          firestore,
          `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_INVOICES_TABLE}`,
        );

        // Query 1: Regular invoices with farmorderDeliveryDate
        const regularInvoicesQuery = query(
          invoicesCollection,
          where("farmorderDeliveryDate", ">=", startDate),
          where("farmorderDeliveryDate", "<=", endDate),
        );

        // Query 2: Standalone credit invoices with invoiceDate
        const standaloneCreditQuery = query(
          invoicesCollection,
          where("invoiceDate", ">=", startDate),
          where("invoiceDate", "<=", endDate),
          where("invoiceType", "==", "credit"),
        );

        let regularInvoices = [];
        let standaloneCredits = [];
        let regularLoaded = false;
        let creditsLoaded = false;

        const mergeAndSend = () => {
          if (regularLoaded && creditsLoaded) {
            // Merge both arrays and remove duplicates based on id
            const allInvoices = [...regularInvoices, ...standaloneCredits];
            const uniqueInvoices = Array.from(new Map(allInvoices.map((inv) => [inv.id, inv])).values());
            next(null, uniqueInvoices);
          }
        };

        // Listen to regular invoices
        const unsubscribeRegular = onSnapshot(
          regularInvoicesQuery,
          (snapshot) => {
            try {
              regularInvoices = snapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
              }));
              regularLoaded = true;
              mergeAndSend();
            } catch (err) {
              next(err, null);
            }
          },
          (error) => {
            next(error, null);
          },
        );

        // Listen to standalone credit invoices
        const unsubscribeCredits = onSnapshot(
          standaloneCreditQuery,
          (snapshot) => {
            try {
              standaloneCredits = snapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
              }));
              creditsLoaded = true;
              mergeAndSend();
            } catch (err) {
              next(err, null);
            }
          },
          (error) => {
            next(error, null);
          },
        );

        // Return cleanup function
        return () => {
          unsubscribeRegular();
          unsubscribeCredits();
        };
      } catch (error) {
        next(error, null);
      }
    },
  );

  return {
    data,
    error,
    loading: !data && !error,
  };
}
